/* eslint-disable consistent-return */
/**
 * tasksV2DefaultTemplates — Solution A (Default Templates Visualizer) slice.
 *
 * TODO(integration): register this slice in src/redux/store.js by
 * importing the default export from this file and adding it to
 * `rootReducer` as `tasksV2DefaultTemplates: tasksV2DefaultTemplatesReducer`.
 * The DefaultTemplatesPanel mounting in PracticeCockpit.jsx will also
 * happen in the integration pass after all parallel agents finish --
 * see SOLUTION_A_RESULT.md for the full hand-off list.
 *
 * Why this is a STANDALONE slice and NOT an addition to the existing
 * `tasksV2Migration.js` slice:
 *   * Two other parallel agents are working in `tasksV2Migration.js`
 *     (Solutions B and C). Editing that file would create merge
 *     contention.
 *   * Our state shape and loading flags are self-contained -- there's
 *     no overlap with the master cockpit state.
 *   * Keeping the slice scoped to "system templates" makes it trivial
 *     to delete or refactor when post-cutover the visualizer is
 *     replaced by `TemplateLibrary`.
 *
 * State shape:
 *
 *     {
 *         byPractice: {
 *             [practiceId]: {
 *                 templates: [...],          // 6 SYSTEM defaults (or fewer if some missing)
 *                 missing_groups: [...],     // V1 group strings with no resolved template
 *                 channel_patch_table: {...},// BE-owned method -> {item_type, title} map
 *                 loading: bool,
 *                 error: string|null,
 *                 fetchedAt: number|null,    // Date.now() of last successful response
 *                 featureAvailable: bool,    // false on 404 (matches TemplateSelectionPanel pattern)
 *             }
 *         }
 *     }
 *
 * Endpoint:
 *   GET /api/v1/management/tasks-migration/practices/<id>/system-templates/
 *
 * 404 handling: BE returns 404 when the practice doesn't exist (see
 * `apps/management/tasks_migration_system_templates_views.py`). The FE
 * treats that as `featureAvailable=false` so the panel renders null --
 * mirrors the pattern used by `TemplateSelectionPanel.jsx` for its
 * own 404 handling.
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch as appDispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';


const BASE = '/api/v1/management/tasks-migration';


const _emptyEntry = () => ({
    templates: [],
    missing_groups: [],
    channel_patch_table: {},
    loading: false,
    error: null,
    fetchedAt: null,
    featureAvailable: true,
});


const initialState = {
    byPractice: {},
};


const slice = createSlice({
    name: 'tasksV2DefaultTemplates',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { practiceId, loading } = action.payload;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = _emptyEntry();
            }
            state.byPractice[practiceId].loading = !!loading;
            if (loading) {
                state.byPractice[practiceId].error = null;
            }
        },
        setSnapshot(state, action) {
            const {
                practiceId, templates, missing_groups: missingGroups,
                channel_patch_table: channelPatchTable,
            } = action.payload;
            state.byPractice[practiceId] = {
                ..._emptyEntry(),
                templates: templates || [],
                missing_groups: missingGroups || [],
                channel_patch_table: channelPatchTable || {},
                loading: false,
                error: null,
                fetchedAt: Date.now(),
                featureAvailable: true,
            };
        },
        setError(state, action) {
            const { practiceId, error } = action.payload;
            if (!state.byPractice[practiceId]) {
                state.byPractice[practiceId] = _emptyEntry();
            }
            state.byPractice[practiceId].error = error;
            state.byPractice[practiceId].loading = false;
        },
        setUnavailable(state, action) {
            // 404 on the BE -> render null. Match the TemplateSelectionPanel
            // pattern: keep the entry but flag it featureAvailable=false.
            const { practiceId } = action.payload;
            state.byPractice[practiceId] = {
                ..._emptyEntry(),
                featureAvailable: false,
                loading: false,
                fetchedAt: Date.now(),
            };
        },
        clearPractice(state, action) {
            const { practiceId } = action.payload;
            delete state.byPractice[practiceId];
        },
    },
});


export const {
    setLoading,
    setSnapshot,
    setError,
    setUnavailable,
    clearPractice,
} = slice.actions;


/**
 * GET /practices/{id}/system-templates/
 *
 * Hydrates `state.tasksV2DefaultTemplates.byPractice[practiceId]` with
 * the 6 SYSTEM defaults + missing_groups + channel_patch_table.
 *
 * 404 -> setUnavailable (panel renders null).
 * Other non-200 -> setError (panel can show a quiet warning).
 *
 * Accepts an optional `dispatchFn` arg for tests; defaults to the
 * app-wide dispatch from the store.
 */
export function fetchSystemTemplates(practiceId, dispatchFn = appDispatch) {
    return async () => {
        if (!practiceId) return null;
        dispatchFn(slice.actions.setLoading({ practiceId, loading: true }));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/system-templates/`,
            );
            if (response.status === 404) {
                dispatchFn(slice.actions.setUnavailable({ practiceId }));
                return null;
            }
            if (response.status !== 200) {
                dispatchFn(slice.actions.setError({
                    practiceId,
                    error: `Failed to load system templates (HTTP ${response.status})`,
                }));
                return null;
            }
            dispatchFn(slice.actions.setSnapshot({
                practiceId,
                templates: response.data?.templates ?? [],
                missing_groups: response.data?.missing_groups ?? [],
                channel_patch_table: response.data?.channel_patch_table ?? {},
            }));
            return response.data;
        } catch (error) {
            dispatchFn(slice.actions.setError({
                practiceId,
                error: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatchFn(slice.actions.setLoading({ practiceId, loading: false }));
        }
    };
}


export default slice.reducer;
